<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useDebounceFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PilotKnowledgeBasesAPI from 'dashboard/api/pilot/knowledgeBases';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CreateKnowledgeBaseDialog from './CreateKnowledgeBaseDialog.vue';

const props = defineProps({
  // 助手 ID
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const searchQuery = ref('');
const createKnowledgeBaseDialogRef = ref(null);
const processingIds = ref(new Set()); // 正在处理的知识库 ID
const batchSelectedIds = ref(new Set()); // 批量选中的知识库 ID
const isBatchSubmitting = ref(false);
const isCreateDialogOpen = ref(false); // 子弹窗是否打开
const hoveredKbId = ref(null); // hover 状态的知识库 ID

// 分页状态
const pageSize = 10;
const currentPage = ref(1);
const listContainerRef = ref(null);
const knowledgeBases = ref([]); // 本地列表数据
const totalCount = ref(0);
const isLoading = ref(false);
const isLoadingMore = ref(false);

// 是否还有更多数据
const hasMore = computed(() => {
  return knowledgeBases.value.length < totalCount.value;
});

// 从 store 获取已关联的知识库列表
const assistantKnowledgeBases = useMapGetter(
  'pilotAssistantKnowledgeBases/getRecords'
);

// 已关联的知识库 ID 集合（用于在"全部" tab 中标记已关联的知识库）
const linkedIdsSet = computed(() => {
  const ids = (assistantKnowledgeBases.value || []).map(kb => kb.id);
  return new Set(ids);
});

// 显示的知识库列表（始终显示所有知识库）
const displayedKnowledgeBases = computed(() => {
  return knowledgeBases.value;
});

// 刷新关联列表
const refreshLinkedKnowledgeBases = async () => {
  await store.dispatch('pilotAssistantKnowledgeBases/get', {
    assistantId: props.assistantId,
  });
};

// 加载知识库列表
const fetchKnowledgeBases = async (page = 1, append = false) => {
  if (append) {
    isLoadingMore.value = true;
  } else {
    isLoading.value = true;
  }

  try {
    const params = { page, per_page: pageSize, search: searchQuery.value };
    const { data } = await PilotKnowledgeBasesAPI.get(params);

    if (append) {
      knowledgeBases.value = [...knowledgeBases.value, ...data.payload];
    } else {
      knowledgeBases.value = data.payload;
    }
    totalCount.value =
      data.meta?.total_count || data.meta?.count || data.meta?.total || 0;
    currentPage.value = page;
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
};

// 加载更多
const loadMore = () => {
  if (hasMore.value && !isLoadingMore.value) {
    fetchKnowledgeBases(currentPage.value + 1, true);
  }
};

// 滚动加载更多
const handleScroll = event => {
  const container = event.target;
  const scrollBottom =
    container.scrollHeight - container.scrollTop - container.clientHeight;

  if (scrollBottom < 50 && hasMore.value && !isLoadingMore.value) {
    loadMore();
  }
};

// 判断知识库是否已关联
const isLinked = kbId => linkedIdsSet.value.has(kbId);

// 判断知识库是否正在处理中
const isProcessing = kbId => processingIds.value.has(kbId);

// 批量选择相关
const isSelected = kbId => batchSelectedIds.value.has(kbId);

const toggleSelection = kbId => {
  if (batchSelectedIds.value.has(kbId)) {
    batchSelectedIds.value.delete(kbId);
  } else {
    batchSelectedIds.value.add(kbId);
  }
  batchSelectedIds.value = new Set(batchSelectedIds.value);
};

const hasSelection = computed(() => batchSelectedIds.value.size > 0);

// 全选/取消全选标签
const buildSelectedCountLabel = computed(() => {
  const count = displayedKnowledgeBases.value.length;
  const isAllSelected = batchSelectedIds.value.size === count && count > 0;
  return isAllSelected ? `取消全选 (${count})` : `全选 (${count})`;
});

const selectedCountLabel = computed(() => {
  return `已选择 ${batchSelectedIds.value.size} 项`;
});

// 全选/取消全选
const toggleSelectAll = () => {
  const allItems = displayedKnowledgeBases.value;
  const allSelected =
    batchSelectedIds.value.size === allItems.length && allItems.length > 0;

  if (allSelected) {
    batchSelectedIds.value = new Set();
  } else {
    batchSelectedIds.value = new Set(allItems.map(kb => kb.id));
  }
};

// 获取知识库类型标签
const getTypeLabel = kb => {
  return kb.knowledge_base_type === 'faq'
    ? t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.TYPE.FAQ')
    : t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.TYPE.DOCUMENT');
};

// 获取知识库类型颜色类
const getTypeColorClass = kb => {
  return kb.knowledge_base_type === 'faq'
    ? 'bg-n-emerald-100 text-n-emerald-700 dark:bg-n-emerald-900/30 dark:text-n-emerald-400'
    : 'bg-n-blue-100 text-n-blue-700 dark:bg-n-blue-900/30 dark:text-n-blue-400';
};

// 打开创建知识库弹窗
const openCreateDialog = () => {
  isCreateDialogOpen.value = true;
  createKnowledgeBaseDialogRef.value?.open();
};

// 子弹窗关闭时的回调
const handleCreateDialogClose = () => {
  isCreateDialogOpen.value = false;
};

// 知识库创建完成后的回调
const handleKnowledgeBaseCreated = newKnowledgeBase => {
  if (newKnowledgeBase) {
    knowledgeBases.value = [newKnowledgeBase, ...knowledgeBases.value];
    totalCount.value++;
    nextTick(() => {
      if (listContainerRef.value) {
        listContainerRef.value.scrollTop = 0;
      }
    });
  }
};

// 切换知识库关联状态
const toggleKnowledgeBaseLink = async (kb, action) => {
  processingIds.value.add(kb.id);
  processingIds.value = new Set(processingIds.value);

  try {
    const dispatchArgs = { assistantId: props.assistantId };
    if (action === 'add') {
      dispatchArgs.knowledgeBaseId = kb.id;
      await store.dispatch('pilotAssistantKnowledgeBases/create', dispatchArgs);
      useAlert(t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.ADD.SUCCESS'));
    } else {
      dispatchArgs.id = kb.id;
      await store.dispatch('pilotAssistantKnowledgeBases/delete', dispatchArgs);
      useAlert(t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.DELETE.SUCCESS'));
    }
    await refreshLinkedKnowledgeBases();
  } catch (error) {
    useAlert(
      error?.message ||
        t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.OPERATION_ERROR')
    );
  } finally {
    processingIds.value.delete(kb.id);
    processingIds.value = new Set(processingIds.value);
  }
};

// 关闭弹窗（由 Dialog 的 @close 事件触发）
const handleClose = () => {
  emit('close');
};

// 主动关闭弹窗（点击按钮时调用）
const closeDialog = () => {
  dialogRef.value?.close();
};

// 打开弹窗
const open = async () => {
  searchQuery.value = '';
  batchSelectedIds.value = new Set();
  knowledgeBases.value = [];
  totalCount.value = 0;
  currentPage.value = 1;
  dialogRef.value?.open();
  // 先刷新已关联的知识库列表，确保数据是最新的
  await refreshLinkedKnowledgeBases();
  fetchKnowledgeBases(1, false);
};

// 搜索防抖
const debouncedSearch = useDebounceFn(() => {
  knowledgeBases.value = [];
  totalCount.value = 0;
  currentPage.value = 1;
  fetchKnowledgeBases(1, false);
  nextTick(() => {
    if (listContainerRef.value) {
      listContainerRef.value.scrollTop = 0;
    }
  });
}, 300);

watch(searchQuery, () => {
  debouncedSearch();
});

// 批量操作
const handleBatchOperation = async action => {
  if (!hasSelection.value || isBatchSubmitting.value) return;

  isBatchSubmitting.value = true;
  try {
    let ids = Array.from(batchSelectedIds.value);

    // 添加时过滤掉已关联的知识库
    if (action === 'add') {
      ids = ids.filter(id => !linkedIdsSet.value.has(id));
    }
    // 移除时过滤掉未关联的知识库
    if (action === 'remove') {
      ids = ids.filter(id => linkedIdsSet.value.has(id));
    }

    if (ids.length === 0) {
      batchSelectedIds.value = new Set();
      return;
    }

    const dispatchAction =
      action === 'add'
        ? 'pilotAssistantKnowledgeBases/create'
        : 'pilotAssistantKnowledgeBases/delete';

    await Promise.all(
      ids.map(kbId => {
        const args = { assistantId: props.assistantId };
        if (action === 'add') {
          args.knowledgeBaseId = kbId;
        } else {
          args.id = kbId;
        }
        return store.dispatch(dispatchAction, args);
      })
    );
    await refreshLinkedKnowledgeBases();
    batchSelectedIds.value = new Set();
    if (action === 'add') {
      useAlert(t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.ADD.SUCCESS'));
    } else {
      useAlert(t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.DELETE.SUCCESS'));
    }
  } catch (error) {
    useAlert(
      error?.message ||
        t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.OPERATION_ERROR')
    );
  } finally {
    isBatchSubmitting.value = false;
  }
};

defineExpose({
  dialogRef,
  open,
  close: handleClose,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="xl"
    overflow-y-auto
    :disable-click-outside="isCreateDialogOpen"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <!-- 搜索工具条 - 无选中时显示 -->
      <div v-if="!hasSelection" class="flex items-center gap-2">
        <Input
          v-model="searchQuery"
          type="search"
          size="sm"
          :placeholder="
            t(
              'PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.SEARCH_PLACEHOLDER'
            )
          "
          :custom-input-class="[
            'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
          ]"
          class="flex-1"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
            />
          </template>
        </Input>
        <div class="relative">
          <Button
            :label="
              t(
                'PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.CREATE_BUTTON'
              )
            "
            icon="i-lucide-plus"
            variant="ghost"
            color="slate"
            size="sm"
            @click="openCreateDialog"
          />
          <span
            class="absolute -top-2 -right-2 px-1.5 py-0.5 text-xs font-medium text-white whitespace-nowrap rounded-tl-lg rounded-tr-lg rounded-br-lg bg-n-ruby-9"
          >
            {{
              t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.CREATE_BADGE')
            }}
          </span>
        </div>
      </div>

      <!-- 批量操作工具条 - 有选中时显示 -->
      <div v-else class="flex items-center gap-3 px-3">
        <div class="flex items-center gap-2">
          <Checkbox
            :model-value="
              batchSelectedIds.size === displayedKnowledgeBases.length &&
              displayedKnowledgeBases.length > 0
            "
            :indeterminate="
              batchSelectedIds.size > 0 &&
              batchSelectedIds.size < displayedKnowledgeBases.length
            "
            @update:model-value="toggleSelectAll"
          />
          <span class="text-sm font-medium text-n-slate-12 tabular-nums">
            {{ buildSelectedCountLabel }}
          </span>
        </div>
        <span class="text-sm text-n-slate-10 truncate tabular-nums">
          {{ selectedCountLabel }}
        </span>
        <div class="h-4 w-px bg-n-strong" />
        <Button
          label="批量添加"
          size="sm"
          variant="ghost"
          color="slate"
          :is-loading="isBatchSubmitting"
          @click="handleBatchOperation('add')"
        />
        <Button
          label="批量移除"
          size="sm"
          variant="ghost"
          color="ruby"
          :is-loading="isBatchSubmitting"
          @click="handleBatchOperation('remove')"
        />
      </div>

      <!-- 知识库列表 -->
      <div
        ref="listContainerRef"
        class="flex flex-col gap-2 max-h-96 overflow-y-auto"
        @scroll="handleScroll"
      >
        <!-- 加载中 -->
        <div v-if="isLoading" class="flex justify-center py-8">
          <Spinner :size="20" />
        </div>

        <!-- 空状态 -->
        <div
          v-else-if="displayedKnowledgeBases.length === 0"
          class="text-sm text-n-slate-9 text-center py-8"
        >
          {{
            searchQuery
              ? t(
                  'PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.EMPTY_SEARCH'
                )
              : t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.EMPTY_LIST')
          }}
        </div>

        <!-- 知识库列表 -->
        <template v-else>
          <div
            v-for="kb in displayedKnowledgeBases"
            :key="kb.id"
            class="flex items-center gap-3 p-3 rounded-lg border transition-colors"
            :class="[
              isSelected(kb.id)
                ? 'border-n-brand bg-n-brand-50 dark:bg-n-brand-500/10'
                : 'border-n-slate-6 hover:border-n-slate-8 hover:bg-n-slate-1 dark:hover:bg-n-slate-800',
            ]"
            @mouseenter="hoveredKbId = kb.id"
            @mouseleave="hoveredKbId = null"
          >
            <!-- Checkbox - hover或有选中时才显示 -->
            <div
              :class="{
                invisible: hoveredKbId !== kb.id && batchSelectedIds.size === 0,
              }"
            >
              <Checkbox
                :model-value="isSelected(kb.id)"
                @update:model-value="toggleSelection(kb.id)"
                @click.stop
              />
            </div>

            <!-- 知识库 Logo -->
            <div
              class="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
              :class="kb.logo_url ? '' : 'bg-n-brand/10'"
            >
              <img
                v-if="kb.logo_url"
                :src="kb.logo_url"
                :alt="kb.name"
                class="w-full h-full object-cover"
                @error="$event.target.style.display = 'none'"
              />
              <span
                v-if="!kb.logo_url"
                class="text-sm font-semibold text-n-brand"
              >
                {{ kb.name?.charAt(0)?.toUpperCase() || '?' }}
              </span>
            </div>

            <!-- 知识库信息 -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-sm text-n-slate-12 truncate">
                  {{ kb.name }}
                </span>
                <span
                  v-if="kb.knowledge_base_type === 'faq'"
                  class="px-2 py-0.5 text-xs rounded-full font-medium"
                  :class="getTypeColorClass(kb)"
                >
                  {{ getTypeLabel(kb) }}
                </span>
                <!-- 已关联标记 -->
                <span
                  v-if="isLinked(kb.id)"
                  class="px-1.5 py-0.5 text-xs font-medium text-white whitespace-nowrap rounded bg-n-ruby-9"
                >
                  已关联
                </span>
              </div>
              <div class="text-xs text-n-slate-10 mt-0.5 truncate">
                {{
                  kb.description ||
                  t(
                    'PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.NO_DESCRIPTION'
                  )
                }}
              </div>
            </div>

            <!-- 文档和FAQ数量 -->
            <div
              class="flex items-center gap-2 flex-shrink-0 text-xs text-n-slate-9"
            >
              <div class="flex items-center gap-1">
                <span class="i-lucide-file-text text-sm" />
                <span>{{ kb.documents_count || 0 }}</span>
              </div>
              <div class="flex items-center gap-1">
                <span class="i-lucide-message-circle-question text-sm" />
                <span>{{ kb.faqs_count || 0 }}</span>
              </div>
            </div>

            <!-- 添加/移除按钮 -->
            <Button
              v-if="isLinked(kb.id)"
              icon="i-lucide-minus"
              size="sm"
              variant="faded"
              color="ruby"
              :is-loading="isProcessing(kb.id)"
              :disabled="isProcessing(kb.id)"
              @click="toggleKnowledgeBaseLink(kb, 'remove')"
            />
            <Button
              v-else
              icon="i-lucide-plus"
              size="sm"
              variant="faded"
              color="slate"
              :is-loading="isProcessing(kb.id)"
              :disabled="isProcessing(kb.id)"
              @click="toggleKnowledgeBaseLink(kb, 'add')"
            />
          </div>

          <!-- 加载更多 -->
          <div v-if="hasMore" class="flex justify-center py-3">
            <Button
              :label="
                t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.LOAD_MORE')
              "
              variant="ghost"
              color="slate"
              size="sm"
              :is-loading="isLoadingMore"
              :disabled="isLoadingMore"
              @click="loadMore"
            />
          </div>
        </template>
      </div>

      <!-- 底部按钮 -->
      <div
        class="flex items-center justify-end gap-2 pt-4 border-t border-n-slate-6"
      >
        <Button
          :label="t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.CLOSE')"
          variant="faded"
          color="slate"
          @click="closeDialog"
        />
      </div>
    </div>
  </Dialog>

  <!-- 创建知识库弹窗 -->
  <CreateKnowledgeBaseDialog
    ref="createKnowledgeBaseDialogRef"
    @created="handleKnowledgeBaseCreated"
    @close="handleCreateDialogClose"
  />
</template>
